/**
 * Registering the service worker and the update flow (08-clients.md "Updates"): a new
 * build installs in the background and waits; this tells the page ("A new version is
 * ready") and, when the person taps Update, asks the waiting worker to take over and
 * reloads once it has. Nothing reloads by itself, so a cashier mid-order is never
 * interrupted.
 */
import { useSyncExternalStore } from 'react';

export type UpdateState = 'idle' | 'ready' | 'applying';

/** The parts of `ServiceWorkerContainer` / registration this uses (faked in tests). */
export interface WorkerLike {
  state: string;
  postMessage(message: unknown): void;
  addEventListener(type: 'statechange', listener: () => void): void;
}
export interface RegistrationLike {
  waiting: WorkerLike | null;
  installing: WorkerLike | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
  update(): Promise<unknown>;
}
export interface ContainerLike {
  controller: unknown;
  register(url: string, options?: { scope?: string }): Promise<RegistrationLike>;
  addEventListener(type: 'controllerchange', listener: () => void): void;
}

export class UpdateManager {
  #state: UpdateState = 'idle';
  #waiting: WorkerLike | null = null;
  readonly #listeners = new Set<() => void>();
  #timer: ReturnType<typeof setInterval> | undefined;

  constructor(
    private readonly container: ContainerLike,
    private readonly reload: () => void,
    private readonly checkEveryMs = 30 * 60_000,
  ) {}

  get state(): UpdateState {
    return this.#state;
  }

  subscribe = (l: () => void): (() => void) => {
    this.#listeners.add(l);
    return () => {
      this.#listeners.delete(l);
    };
  };

  #set(next: UpdateState): void {
    if (next === this.#state) return;
    this.#state = next;
    this.#listeners.forEach((l) => l());
  }

  /** Register `/sw.js`; resolves with the registration. */
  async start(url = '/sw.js'): Promise<RegistrationLike> {
    const reg = await this.container.register(url, { scope: '/' });
    // A worker already waiting from an earlier visit; only an update when a worker controls
    // the page (the first install is not an update).
    if (reg.waiting && this.container.controller) this.#offer(reg.waiting);
    reg.addEventListener('updatefound', () => {
      const w = reg.installing;
      w?.addEventListener('statechange', () => {
        if (w.state === 'installed' && this.container.controller) this.#offer(w);
      });
    });
    this.container.addEventListener('controllerchange', () => {
      if (this.#state === 'applying') this.reload();
    });
    this.#timer = setInterval(() => void reg.update().catch(() => undefined), this.checkEveryMs);
    return reg;
  }

  stop(): void {
    clearInterval(this.#timer);
  }

  #offer(worker: WorkerLike): void {
    this.#waiting = worker;
    this.#set('ready');
  }

  /** The person agreed: the waiting worker takes over and the page reloads on the switch. */
  apply(): void {
    if (!this.#waiting) return;
    this.#set('applying');
    this.#waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

let manager: UpdateManager | null = null;

/** Register in production builds only (the dev server serves modules, not a shell). */
export function startServiceWorker(): void {
  if (manager || !import.meta.env.PROD || typeof navigator === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  manager = new UpdateManager(navigator.serviceWorker as unknown as ContainerLike, () =>
    window.location.reload(),
  );
  void manager.start().catch(() => {
    manager = null;
  });
}

const noop = () => () => undefined;

export function useUpdateState(): { state: UpdateState; apply(): void } {
  const m = manager;
  const state = useSyncExternalStore(
    m ? m.subscribe : noop,
    () => m?.state ?? 'idle',
    () => 'idle' as const,
  );
  return { state, apply: () => m?.apply() };
}

/** Tests: use a manager they control. */
export function setUpdateManager(next: UpdateManager | null): void {
  manager = next;
}
